import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate } from '@angular/service-worker';
import { environment } from '../../../environments/environment';

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

@Injectable({ providedIn: 'root' })
export class PwaService {
  private readonly updates = inject(SwUpdate);
  private readonly destroyRef = inject(DestroyRef);
  private promptEvent: InstallPromptEvent | null = null;
  private checking = false;
  private lastCheck = 0;
  readonly enabled = environment.pwaEnabled;
  readonly online = signal(navigator.onLine);
  // Latched until a user-requested reload: cached in-memory data is not fresh after reconnect.
  readonly connectionLost = signal(this.enabled && !navigator.onLine);
  readonly installed = signal(this.isStandalone());
  readonly installAvailable = signal(false);
  readonly installing = signal(false);
  readonly iosHelp = signal(false);
  readonly installDismissed = signal(false);
  readonly updateReady = signal(false);
  readonly updateDismissed = signal(false);
  readonly recoveryRequired = signal(false);
  readonly error = signal('');
  readonly ios = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  readonly canInstall = computed(() => this.enabled && !this.installed() &&
    !this.installDismissed() && (this.installAvailable() || this.ios));

  constructor() {
    if (!this.enabled) return;
    this.listen(window, 'offline', () => {
      this.online.set(false);
      this.connectionLost.set(true);
    });
    this.listen(window, 'online', () => {
      this.online.set(true);
      void this.checkForUpdate();
    });
    this.listen(window, 'lom:connection-lost', () => this.connectionLost.set(true));
    this.listen(window, 'beforeinstallprompt', (event) => {
      event.preventDefault();
      this.promptEvent = event as InstallPromptEvent;
      this.installAvailable.set(true);
      this.installDismissed.set(false);
    });
    this.listen(window, 'appinstalled', () => {
      this.installed.set(true);
      this.promptEvent = null;
      this.installAvailable.set(false);
      this.iosHelp.set(false);
    });
    const display = window.matchMedia('(display-mode: standalone)');
    this.listen(display, 'change', () => this.installed.set(this.isStandalone()));
    this.listen(document, 'visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.checkForUpdate();
    });
    if (this.updates.isEnabled) {
      this.updates.versionUpdates.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
        if (event.type === 'VERSION_READY') {
          this.updateReady.set(true);
          this.updateDismissed.set(false);
          this.error.set('');
        } else if (event.type === 'VERSION_INSTALLATION_FAILED') {
          this.error.set('No se ha podido preparar la actualización. Puedes volver a comprobarla.');
        }
      });
      this.updates.unrecoverable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.recoveryRequired.set(true);
        this.error.set('Esta versión necesita recargarse. Guarda o termina tus operaciones antes de continuar.');
      });
      // Angular checks on navigation too. Do not start polling before registration/stability.
      const timer = window.setInterval(() => void this.checkForUpdate(), 6 * 60 * 60 * 1000);
      this.destroyRef.onDestroy(() => window.clearInterval(timer));
    }
  }

  async checkForUpdate(force = false): Promise<void> {
    if (!this.enabled || !this.updates.isEnabled || !this.online() || this.checking) return;
    if (!force && Date.now() - this.lastCheck < 60_000) return;
    this.checking = true;
    this.lastCheck = Date.now();
    try {
      await this.updates.checkForUpdate();
      if (!this.recoveryRequired()) this.error.set('');
    } catch {
      this.error.set('No se pudo comprobar la actualización. Reinténtalo cuando tengas conexión.');
    } finally {
      this.checking = false;
    }
  }

  async install(): Promise<void> {
    if (this.installed() || this.installing()) return;
    if (!this.promptEvent) {
      if (this.ios) this.iosHelp.set(!this.iosHelp());
      return;
    }
    const event = this.promptEvent;
    this.promptEvent = null;
    this.installAvailable.set(false);
    this.installing.set(true);
    try {
      await event.prompt();
      const choice = await event.userChoice;
      if (choice.outcome === 'accepted') this.installed.set(true);
    } catch {
      this.error.set('No se pudo abrir la instalación. Puedes utilizar el menú del navegador.');
    } finally {
      this.installing.set(false);
    }
  }

  reload(): void {
    if (!this.online()) return;
    if (window.confirm('Se recargará League of Managers. Los cambios sin guardar se perderán. Si hay una operación pendiente, espera a que termine. ¿Continuar?')) {
      // Never activateUpdate(): a reload keeps the shell and lazy chunks in one version.
      window.location.reload();
    }
  }

  private isStandalone(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true;
  }

  private listen(target: EventTarget, type: string, listener: EventListener): void {
    target.addEventListener(type, listener);
    this.destroyRef.onDestroy(() => target.removeEventListener(type, listener));
  }
}
